// src/components/PublicBusinessClubSignup.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Turnstile } from '@marsidev/react-turnstile';
import { Building2, Loader2, Ticket, CheckCircle2, Copy, Check, AlertCircle, Globe, ExternalLink } from 'lucide-react';

/** Tenant IBE (Internet Booking Engine) — embedded on the success screen so new
 * members can book immediately and redeem their promo code. */
const IBE_URL = 'https://www.secure-hotel-booking.com/d-edge/ADAPT-Apartments-Berlin-Adlershof/J6MC/HotelSelection';

/**
 * PUBLIC BusinessClub sign-up page (no auth — mounted in AppRouter.jsx before
 * any auth checks, mirroring the /offer/ pattern).
 *
 * Flow:
 *   1. Guest fills the form (first/last name, email, company, optional phone,
 *      DSGVO marketing-consent checkbox).
 *   2. POST /api/business-club — server validates, rate-limits, dedupes
 *      (unique tenant_id + email) and inserts via the service-role client.
 *   3. Success screen shows the tenant's shared promo code on-screen; the
 *      server additionally sends a confirmation email via Resend (fail-soft).
 *   4. Duplicate sign-ups get a friendly message and the code re-shown
 *      (product decision — no error, no second row).
 *
 * @param {Object} props
 * @param {string} props.tenantId - Tenant from the URL (/businessclub/:tenantId).
 */
export default function PublicBusinessClubSignup({ tenantId }: { tenantId: string }) {
  const { t, i18n } = useTranslation();

  // Sign-up language — drives the welcome email language (de|en) and the UI.
  // Initialised from the persisted i18next language (localStorage "i18nextLng").
  const [language, setLanguage] = useState<'de' | 'en'>(i18n.language?.startsWith('en') ? 'en' : 'de');

  const switchLanguage = (lang: 'de' | 'en') => {
    setLanguage(lang);
    i18n.changeLanguage(lang); // persisted via the i18n languageChanged listener
  };

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    marketingConsent: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ duplicate: boolean; promoCode: string | null; firstName: string; pendingEmailConfirmation?: boolean } | null>(null); // { duplicate, promoCode, firstName, pendingEmailConfirmation }
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Cloudflare Turnstile (audit M-1) — widget only rendered when the correct
  // site key is configured; the server enforces the token when its secret is
  // set (TURNSTILE_SECRET_KEY verified in api/business-club.js).
  // SECURITY (audit H-7): the Supabase-auth-captcha fallback was REMOVED -
  // VITE_SUPABASE_TURNSTILE_SITE_KEY belongs to a DIFFERENT key pair whose
  // tokens always FAIL server verification (guaranteed 403s for every
  // submission). No fallback is better than a wrong fallback: with no site
  // key the form submits WITHOUT a token and the server decides (fail-closed
  // in production per audit H-7, fail-open in dev).
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const setField = (field: keyof typeof form, value: string | boolean) => setForm((prev) => ({ ...prev, [field]: value }));

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.firstName.trim()) next.firstName = t('businessClub.fieldRequired');
    if (!form.lastName.trim()) next.lastName = t('businessClub.fieldRequired');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = t('businessClub.invalidEmail');
    }
    if (!form.company.trim()) next.company = t('businessClub.fieldRequired');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setGeneralError(null);
    try {
      const res = await fetch('/api/business-club', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, tenantId, language, turnstileToken: captchaToken || undefined }),
      });
      setCaptchaToken(null); // tokens are single-use — force a fresh one
      if (res.status === 429) {
        setGeneralError(t('businessClub.rateLimited'));
        return;
      }
      if (!res.ok) {
        setGeneralError(t('businessClub.signUpError'));
        return;
      }
      const data = await res.json();
      setResult({
        duplicate: !!data.duplicate,
        promoCode: data.promoCode || null,
        firstName: data.member?.firstName || form.firstName,
        // Double opt-in (audit M-8): the server no longer returns the promo
        // code for fresh signups — it arrives by email AFTER the confirmation
        // link is clicked. The UI shows the "check your inbox" state.
        pendingEmailConfirmation: !!data.pendingEmailConfirmation,
      });
    } catch (err) {
      console.error('[PublicBusinessClubSignup] sign-up failed:', err);
      setGeneralError(t('businessClub.signUpError'));
    } finally {
      setSubmitting(false);
    }
  };

  async function handleCopyCode() {
    if (!result?.promoCode) return;
    try {
      await navigator.clipboard.writeText(result.promoCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — code stays visible.
    }
  }

  const inputCls = 'hub-input w-full py-3 text-sm font-bold rounded-xl shadow-inner outline-hidden transition-all focus:ring-4 focus:ring-[#859F4A]/20';

  // ── Success / duplicate screen ────────────────────────────────────────────
  if (result) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center py-12 px-4">
        <div className="w-full max-w-md flex justify-end mb-4">
          <LanguageToggle language={language} onChange={switchLanguage} />
        </div>
        <div className="w-full max-w-md bg-white rounded-3xl shadow-lg border border-slate-100 p-8 text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle2 size={48} className="text-[#859F4A]" />
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900">
            {result.duplicate
              ? t('businessClub.alreadyMemberTitle')
              : t('businessClub.successTitle', { name: result.firstName })}
          </h2>
          <p className="text-sm text-slate-500 mt-2">
            {result.duplicate
              ? t('businessClub.alreadyMemberText')
              : t('businessClub.successText')}
          </p>

          {/* Double opt-in (audit M-8): code arrives by email AFTER the
              confirmation link is clicked — show the inbox state. */}
          {result.pendingEmailConfirmation && !result.promoCode ? (
            <div className="mt-6">
              <p className="text-sm font-bold text-[#859F4A]">{t('businessClub.optinPendingTitle')}</p>
              <p className="text-xs text-slate-400 mt-2">{t('businessClub.optinPendingText')}</p>
            </div>
          ) : result.promoCode ? (
            <div className="mt-6">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">
                {t('businessClub.yourPromoCode')}
              </p>
              <div className="flex items-center justify-center gap-2 bg-slate-50 border border-slate-200 rounded-2xl py-4 px-4">
                <span className="text-2xl font-black tracking-[0.15em] text-[#859F4A]">
                  {result.promoCode}
                </span>
                <button
                  onClick={handleCopyCode}
                  className="p-2 rounded-lg text-slate-400 hover:text-[#859F4A] transition-colors"
                  title={t('businessClub.copyLink')}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-3">{t('businessClub.promoCodeUsage')}</p>
            </div>
          ) : (
            <p className="text-xs text-slate-400 mt-6">{t('businessClub.codePending')}</p>
          )}
        </div>

        {/* Embedded IBE — book directly and redeem the promo code */}
        <div className="w-full max-w-2xl bg-white rounded-3xl shadow-lg border border-slate-100 p-6 mt-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-lg font-extrabold text-slate-900">{t('businessClub.ibeTitle')}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{t('businessClub.ibeSubtitle')}</p>
            </div>
            <a
              href={IBE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 bg-[#859F4A] hover:bg-[#6c823b] text-white py-2 px-4 rounded-xl font-bold text-xs transition-all shadow-md shrink-0"
            >
              <ExternalLink size={13} />
              {t('businessClub.bookNow')}
            </a>
          </div>
          <iframe
            src={IBE_URL}
            title={t('businessClub.ibeTitle')}
            className="w-full rounded-2xl border border-slate-200 bg-white"
            style={{ height: '640px' }}
            loading="lazy"
          />
        </div>
      </div>
    );
  }

  // ── Sign-up form ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center py-12 px-4">
      <div className="w-full max-w-md flex justify-end mb-4">
        <LanguageToggle language={language} onChange={switchLanguage} />
      </div>
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="flex justify-center">
            <Building2 size={44} className="text-[#859F4A]" />
          </div>
          <h1 className="mt-4 text-3xl font-extrabold text-slate-900">{t('businessClub.publicTitle')}</h1>
          <p className="mt-2 text-sm text-slate-500">{t('businessClub.publicSubtitle')}</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-3xl shadow-lg border border-slate-100 p-8 space-y-4"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
                {t('businessClub.firstName')} *
              </label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setField('firstName', e.target.value)}
                className={inputCls}
                autoComplete="given-name"
              />
              {errors.firstName && <p className="text-[10px] font-bold text-red-500 mt-1">{errors.firstName}</p>}
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
                {t('businessClub.lastName')} *
              </label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setField('lastName', e.target.value)}
                className={inputCls}
                autoComplete="family-name"
              />
              {errors.lastName && <p className="text-[10px] font-bold text-red-500 mt-1">{errors.lastName}</p>}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
              {t('businessClub.email')} *
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              className={inputCls}
              autoComplete="email"
            />
            {errors.email && <p className="text-[10px] font-bold text-red-500 mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
              {t('businessClub.company')} *
            </label>
            <input
              type="text"
              value={form.company}
              onChange={(e) => setField('company', e.target.value)}
              className={inputCls}
              autoComplete="organization"
            />
            {errors.company && <p className="text-[10px] font-bold text-red-500 mt-1">{errors.company}</p>}
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
              {t('businessClub.phone')}
            </label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setField('phone', e.target.value)}
              className={inputCls}
              autoComplete="tel"
            />
          </div>

          <div>
            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.marketingConsent}
                onChange={(e) => setField('marketingConsent', e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-[#859F4A]"
              />
              <span className="text-xs text-slate-500 leading-snug">
                {t('businessClub.consentText')}
              </span>
            </label>
          </div>

          {generalError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-xs font-bold text-red-600">
              <AlertCircle size={14} className="shrink-0" />
              {generalError}
            </div>
          )}

          {siteKey && (
            <div className="flex justify-center">
              <Turnstile
                siteKey={siteKey}
                // SECURITY (audit L-9): action binding — the token is
                // bound to this form; the backend asserts the same action
                // in the siteverify response, blocking cross-form replay.
                options={{ action: 'business-club-signup' }}
                onSuccess={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken(null)}
                onError={() => setCaptchaToken(null)}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || (!!siteKey && !captchaToken)}
            className="w-full bg-[#859F4A] hover:bg-[#6c823b] text-white py-3.5 px-4 rounded-xl font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Ticket size={16} />}
            {t('businessClub.signUpButton')}
          </button>
        </form>
      </div>
    </div>
  );
}

/**
 * DE/EN language switcher for the public sign-up page. Changing the language
 * re-renders all copy via i18next AND is sent with the sign-up so the welcome
 * email is delivered in the guest's language.
 */
function LanguageToggle({ language, onChange }: { language: 'de' | 'en'; onChange: (lang: 'de' | 'en') => void }) {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center gap-1 bg-white border border-slate-200 rounded-full p-1 shadow-xs"
      role="group"
      aria-label={t('businessClub.languageLabel')}
    >
      <Globe size={13} className="text-slate-400 ml-2 shrink-0" />
      {(['de', 'en'] as const).map((lang) => (
        <button
          key={lang}
          type="button"
          onClick={() => onChange(lang)}
          aria-pressed={language === lang}
          className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest transition-colors ${
            language === lang ? 'bg-[#859F4A] text-white' : 'text-slate-400 hover:text-slate-600'
          }`}
        >
          {t(`businessClub.lang_${lang}`)}
        </button>
      ))}
    </div>
  );
}
